import { useState } from 'react'

import { Collapse, Modal, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import {
  AlertCircle,
  Check,
  Info,
  Mail,
  Phone,
  Printer,
  Zap,
} from 'lucide-react'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import DNButton from 'src/components/Buttons/Button/DNButton'

import {
  blockedReasonLine,
  panelState,
  stepDateLabel,
  stepStatusLabel,
  type OutreachSchedulePreview,
  type OutreachScheduleStep,
} from './outreachSchedule'

export interface AutomatedOutreachEmailContent {
  companyName: string
  subject: string
  body: string
  attachments: string[]
}

export interface AutomatedOutreachPanelProps {
  opened: boolean
  employerName: string
  applicantName: string
  initialEmail: string
  preview: OutreachSchedulePreview
  email: AutomatedOutreachEmailContent
  loading?: boolean
  onClose: () => void
  /** Called with the address to hand to automation. */
  onSubmit: (email: string) => void
  /** Called with the address as entered, for the manual composer. */
  onSendManually: (email: string) => void
}

const PRIMARY_LABEL = {
  READY: 'Start Automated Outreach',
  RUNNING: 'Save',
  ENDED: 'Schedule More Attempts',
  BLOCKED: 'Start Automated Outreach',
} as const

const schema = z.object({
  email: z.string().trim().email({ message: 'Enter a valid email address' }),
})

const TYPE_ICON: Record<string, typeof Mail> = {
  EMAIL: Mail,
  CALL: Phone,
  FAX: Printer,
}

const ScheduleRow = ({
  step,
  next = false,
}: {
  step: OutreachScheduleStep
  next?: boolean
}) => {
  const Icon = TYPE_ICON[step.type] ?? Mail
  const done = step.status === 'COMPLETED'
  return (
    <div
      data-testid={next ? 'outreach-next-round-step' : 'outreach-schedule-step'}
      className={`grid grid-cols-[16px_1fr_auto] items-center gap-2.5 border-t border-doubleNickel-gray-100 px-3 py-1.5 text-xs first:border-t-0 ${
        next ? 'bg-doubleNickel-gray-50' : ''
      }`}
    >
      <Icon
        size={14}
        aria-label={step.type.toLowerCase()}
        className={
          done ? 'text-doubleNickel-gray-400' : 'text-doubleNickel-gray-500'
        }
      />
      <span
        className={
          done ? 'text-doubleNickel-gray-400' : 'text-doubleNickel-gray-900'
        }
      >
        {stepDateLabel(step)}
      </span>
      <span className="inline-flex items-center gap-1 text-[11px] text-doubleNickel-gray-500">
        {done && (
          <Check
            size={11}
            strokeWidth={3}
            className="text-doubleNickel-success-600"
          />
        )}
        {stepStatusLabel(step)}
      </span>
    </div>
  )
}

/**
 * What the email icon opens when the company has outreach set up: the
 * address, the steps automation will send, and the email itself. After every
 * step ran it offers another round. When automation won't email the employer
 * it says why, and either lets the recruiter start it anyway or, for a block
 * nobody can start past, sends by hand. Sending by hand is always available.
 */
const AutomatedOutreachPanel = ({
  opened,
  employerName,
  applicantName,
  initialEmail,
  preview,
  email,
  loading = false,
  onClose,
  onSubmit,
  onSendManually,
}: AutomatedOutreachPanelProps) => {
  const [showEmail, setShowEmail] = useState(false)
  const form = useForm<{ email: string }>({
    initialValues: { email: initialEmail },
    validate: zodResolver(schema),
  })
  const state = panelState(preview)
  const blocked = state === 'BLOCKED'
  const hardBlocked = blocked && !preview.overridable
  const sendManually = () => onSendManually(form.values.email.trim())
  const nextRound = preview.nextRound ?? []

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Email ${employerName}`}
      closeOnEscape
      closeOnClickOutside
      // Same placement and stacking as EmailSenderModal, which this replaces
      // when automation will send: above the VoE drawer.
      zIndex={210}
      transitionProps={{ transition: 'slide-up', duration: 300 }}
      overlayProps={{ backgroundOpacity: 0.05 }}
      classNames={{
        title: 'font-semibold',
        body: 'flex flex-col gap-3',
        content:
          'fixed bottom-1.5 right-1.5 m-0 rounded-2xl shadow-lg max-h-[100vh] w-[min(440px,calc(100vw-12px))]',
      }}
    >
      <form
        className="flex flex-col gap-3"
        // A block nobody can start past only ever sends by hand, and the
        // composer checks the address, so it skips this form's validation.
        onSubmit={
          hardBlocked
            ? (event) => {
                event.preventDefault()
                sendManually()
              }
            : form.onSubmit((values) => onSubmit(values.email.trim()))
        }
      >
        <TextInput
          label="Applicant"
          value={applicantName || '—'}
          readOnly
          variant="filled"
        />
        <TextInput
          label="Company"
          value={employerName}
          readOnly
          variant="filled"
        />
        <TextInput
          label="Email"
          placeholder="Enter the employer's email"
          data-autofocus
          {...form.getInputProps('email')}
        />

        {blocked && (
          <div
            data-testid="outreach-blocked-reason"
            className="flex items-start gap-2 rounded-md border border-doubleNickel-warning-200 bg-doubleNickel-warning-50 px-3 py-2 text-xs text-doubleNickel-gray-700"
          >
            <AlertCircle
              size={13}
              aria-hidden
              className="mt-0.5 shrink-0 text-doubleNickel-warning-600"
            />
            <span>
              {blockedReasonLine(
                preview.blockedReason,
                employerName,
                preview.blockedPlatform,
                preview.blockedWindowMonths
              )}
            </span>
          </div>
        )}

        {!hardBlocked && (
          <div className="overflow-hidden rounded-lg border border-doubleNickel-gray-200">
            <div className="flex items-center gap-1.5 border-b border-doubleNickel-brand-100 bg-doubleNickel-brand-25 px-3 py-2 text-xs font-semibold text-doubleNickel-gray-900">
              <Zap size={13} className="text-doubleNickel-brand-600" />
              Automated outreach
              <button
                type="button"
                className="ml-auto text-xs font-medium text-doubleNickel-brand-600 underline decoration-doubleNickel-brand-100 underline-offset-2"
                onClick={() => setShowEmail((v) => !v)}
              >
                {showEmail ? 'Hide email' : 'Preview email'}
              </button>
            </div>
            {preview.steps.map((step, i) => (
              <ScheduleRow key={i} step={step} />
            ))}
            {(state === 'ENDED' || blocked) && nextRound.length > 0 && (
              <>
                <div className="border-t border-dashed border-doubleNickel-brand-100 bg-doubleNickel-gray-50 px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-doubleNickel-brand-600">
                  Next round
                </div>
                {nextRound.map((step, i) => (
                  <ScheduleRow key={`next-${i}`} step={step} next />
                ))}
              </>
            )}
            <Collapse in={showEmail}>
              <div
                data-testid="outreach-email-preview"
                className="flex flex-col gap-2 border-t border-doubleNickel-brand-100 bg-doubleNickel-gray-50 px-3 py-2.5 text-xs text-doubleNickel-gray-700"
              >
                <div className="flex flex-col gap-0.5 text-doubleNickel-gray-500">
                  <span>
                    <b className="font-medium text-doubleNickel-gray-700">
                      From:
                    </b>{' '}
                    {email.companyName}
                    {preview.sender?.fullName
                      ? ` · cc ${preview.sender.fullName}`
                      : ''}
                  </span>
                  <span>
                    <b className="font-medium text-doubleNickel-gray-700">
                      To:
                    </b>{' '}
                    {form.values.email}
                  </span>
                  <span>
                    <b className="font-medium text-doubleNickel-gray-700">
                      Subject:
                    </b>{' '}
                    {email.subject}
                  </span>
                </div>
                <div className="max-h-56 overflow-auto whitespace-pre-line rounded-md border border-doubleNickel-gray-200 bg-white px-2.5 py-2 leading-relaxed">
                  {email.body}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {email.attachments.map((name) => (
                    <span
                      key={name}
                      className="rounded border border-doubleNickel-gray-200 bg-white px-1.5 py-0.5 text-[11px] text-doubleNickel-gray-600"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            </Collapse>
          </div>
        )}

        {state === 'ENDED' && (
          <div className="flex items-start gap-2 rounded-md border border-doubleNickel-gray-300 bg-doubleNickel-gray-50 px-3 py-2 text-xs text-doubleNickel-gray-700">
            <Info
              size={13}
              aria-hidden
              className="mt-0.5 shrink-0 text-doubleNickel-gray-500"
            />
            <span>
              All {preview.steps.length} attempts went out with no answer.
              Schedule another round with the same steps, or change the address
              first.
            </span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-doubleNickel-gray-200 pt-3">
          {!hardBlocked && (
            <button
              type="button"
              className="text-xs text-doubleNickel-brand-600 underline decoration-doubleNickel-brand-100 underline-offset-2"
              onClick={sendManually}
            >
              Send manually
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <DNButton variant="outline" onClick={onClose}>
              Cancel
            </DNButton>
            {hardBlocked ? (
              <DNButton type="submit" variant="primary">
                Send manually
              </DNButton>
            ) : (
              <DNButton
                type="submit"
                variant="primary"
                loading={loading}
                disabled={loading}
              >
                {PRIMARY_LABEL[state] ?? PRIMARY_LABEL.READY}
              </DNButton>
            )}
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default AutomatedOutreachPanel
